import PixleeWidgetInitializer from './PixleeWidgetInitializer';

declare const Pixlee: any;

class PixleeManager {
  private apiKey = '';

  private widgetHtmlSelector = 'pixlee-widget';

  private fallbackWidget?: PixleeWidgetInitializer = undefined;

  private accountId = 1637;

  constructor(accountId: number) {
    this.accountId = accountId;
  }

  init(apiKey: string, mode: 'async' | 'sync') {
    this.apiKey = apiKey;
    if (mode === 'async') {
      window.PixleeAsyncInit = this.syncInit.bind(this);
    } else {
      this.syncInit();
    }
  }

  syncInit() {
    Pixlee.init({
      apiKey: this.apiKey,
    });

    Array.from(document.getElementsByClassName(this.widgetHtmlSelector)).forEach((widget) => {
      this.addPixleeWidgetFromElement(widget);
    });
  }

  buildPixleeWidgetFromElementDataAttr(element: Element, attrPrefix: string) {
    const getNamedItem = (attributePrefix: string, itemName: string) => {
      return element.attributes.getNamedItem(`${attributePrefix}${itemName}`)?.value || '';
    };

    const id = getNamedItem('', 'id');
    const widgetId = parseInt(getNamedItem(`${attrPrefix}-`, 'id') || '0', 10);
    const type = getNamedItem(`${attrPrefix}-`, 'type');
    const skuId = getNamedItem(`${attrPrefix}-`, 'sku');
    const categoryId = getNamedItem(`${attrPrefix}-`, 'category-id');
    const categoryName = getNamedItem(`${attrPrefix}-`, 'category-name');
    const pixleeWidget = new PixleeWidgetInitializer(widgetId, id, type, this.accountId);

    if (skuId) {
      pixleeWidget.setSkuId(parseInt(skuId, 10));
    }

    if (categoryId) {
      pixleeWidget.setCategoryId(parseInt(categoryId, 10));
    }

    if (categoryName) {
      pixleeWidget.setCategoryName(categoryName);
    }

    return pixleeWidget;
  }

  addPixleeWidgetFromElement(element: Element) {
    const widget = this.buildPixleeWidgetFromElementDataAttr(element, 'data-pixlee-widget');

    if (element.attributes.getNamedItem('data-pixlee-widget-fallback-id')) {
      widget.setFallbackWidget(this.buildPixleeWidgetFromElementDataAttr(element, 'data-pixlee-widget-fallback'));
    }

    this.initWidget(widget);
  }

  onMessage(event: any) {
    // only listen to events coming from Pixlee
    let message;
    if (event.data) {
      try {
        message = JSON.parse(event.data);
      } catch (error) {
        return;
      }
    } else {
      return;
    }
    if (message.eventName && message.eventName === 'widgetNumPhotos') {
      if (message.data.photoCount < 1) {
        Pixlee.close();
        window.removeEventListener('message', this.onMessage);
        if (this.fallbackWidget) {
          this.initWidget(this.fallbackWidget);
        }
      }
    }
  }

  initWidget(pixleeWidget: PixleeWidgetInitializer) {
    const widgetOptions = pixleeWidget.toOptions();
    const fallbackWidget = pixleeWidget.getFallbackWidget();
    this.fallbackWidget = fallbackWidget;
    if (fallbackWidget) {
      widgetOptions.subscribedEvents = ['widgetNumPhotos'];
      window.addEventListener('message', this.onMessage.bind(this), false);
    }
    switch (pixleeWidget.getType()) {
      case 'product':
        Pixlee.addProductWidget(widgetOptions);
        break;
      case 'category':
        Pixlee.addCategoryWidget(widgetOptions);
        break;
      default:
        Pixlee.addSimpleWidget(widgetOptions);
    }
    Pixlee.resizeWidget();
  }
}

export default PixleeManager;
